import React, { useState } from 'react';
import { Alert, Button, Col, List} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import usePatch from '../../hooks/update/usePatch';
import useToastContext from '../../hooks/useToastContext';
import { validateStatus } from '../../utils/utils';
import ModalIdentification from './modal-identification';
import { SectionTitleContainer, SectionTitle, SectionTitleActions, NoSpaceDivider, HighlightListItem} from '../styles'
import { IdentificationsFormInputs } from './types/IdentificationsTypes';


const ButtonDelete = ({ record, identifications, setIdentifications, cardHolderId, toast, executePatch }) => {
  function validateIdentification(record) {
    setIdentifications(identifications.filter((item) => item?.number !== record?.number));
  }

  const executePatchDelete = async (record: any) => {
    if (cardHolderId !== null && cardHolderId !== undefined) {
      const jsonPatch = [{ op: 'remove', path: '/identifications/' + record?.id }];

      try {
        const response = await executePatch({ data: jsonPatch });
        if (validateStatus(response?.status)) {
          toast.success('Exito', 'Identificación eliminada correctamente!.');

          validateIdentification(record);
        } else {
          throw new Error();
        }
      } catch (e: unknown) {
        toast.error('Error', `Ocurrió un error, porfavor, intenta nuevamente.`);
      }
    } else {
      validateIdentification(record);
    }
  };

  return (
    <Button
      icon={<DeleteOutlined />}
      size="small"
      type="link"
      onClick={(e) => {
        e.preventDefault();
        executePatchDelete(record);
      }}
    />
  );
};

const Identifications = ({
  identifications,
  setIdentifications,
  cardHolderId,
}: {
  identifications: IdentificationsFormInputs[];
  setIdentifications?: (values: any[]) => void;
  cardHolderId?: string | undefined;
}) => {
  const [identificationsOpen, setModalIdentificationsOpen] = useState(false);
  const [editIdentification, setEditIdentification] = useState(null);
  const toast = useToastContext();
  const { error, executePatch } = usePatch(`v2/card_holders/${cardHolderId}`);

  const onCreateIdentification = (values: IdentificationsFormInputs) => {
    // @ts-ignore
    setIdentifications((prev: any) => {
      return [...prev, values];
    });
    setModalIdentificationsOpen(false);
  };

  const onUpdateIdentification = (value: IdentificationsFormInputs, newValue: IdentificationsFormInputs) => {
    const identificationsFiltered = identifications.filter((item) => !(item.number === value?.number));

    identificationsFiltered.push(newValue);
    setIdentifications(identificationsFiltered);
  };

  const validateAction = async (value: IdentificationsFormInputs) => {
    if (cardHolderId !== null && cardHolderId !== undefined) {
      const jsonPatch = [];

      if (editIdentification !== null) {
        value?.type &&
          jsonPatch.push({
            op: 'replace',
            path: '/identifications/' + editIdentification?.id + '/type',
            value: value?.type,
          });
        value?.number &&
          jsonPatch.push({
            op: 'replace',
            path: '/identifications/' + editIdentification?.id + '/number',
            value: value?.number,
          });
        value?.country &&
          jsonPatch.push({
            op: 'replace',
            path: '/identifications/' + editIdentification?.id + '/country',
            value: value?.country,
          });
        value?.registeredVatPayer &&
          jsonPatch.push({
            op: 'replace',
            path: '/identifications/' + editIdentification?.id + '/registeredVatPayer',
            value: value?.registeredVatPayer,
          });

        try {
          const response = await executePatch({ data: jsonPatch });
          if (validateStatus(response?.status)) {
            toast.success('Exito', 'Identificación actualizada correctamente!.');
            onUpdateIdentification(editIdentification, value);
          } else {
            throw new Error();
          }
        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${error}, porfavor, intenta nuevamente.`);
        }

        setEditIdentification(null);
      } else {
        jsonPatch.push({ op: 'add', path: '/identifications/-', value: value });

        try {
          const response = await executePatch({ data: jsonPatch });
          if (validateStatus(response?.status)) {
            toast.success('Exito', 'Identificación creada correctamente!.');
            onCreateIdentification(value);
          } else {
            throw new Error();
          }
        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${error}, porfavor, intenta nuevamente.`);
        }
      }
    } else {
      onCreateIdentification(value);
    }
    setModalIdentificationsOpen(false);
  };

  const editIdentificationExecute = (record: any) => {
    if (record?.id !== null) {
      setEditIdentification(record);
      setModalIdentificationsOpen(true);
    }
  };

  return (
    <>
      <SectionTitleContainer>
        <SectionTitle> Identificaciones </SectionTitle>
        {(setIdentifications !== undefined) && (
          <SectionTitleActions>
            <Button size="middle" type="link" onClick={() => setModalIdentificationsOpen(true)}>
                Agregar
            </Button>
          </SectionTitleActions>
        )}
      </SectionTitleContainer>
      <NoSpaceDivider />
      {
        identifications?.length > 0 ? (
          <>
            <List
              itemLayout="horizontal"
              dataSource={identifications?.map((identification, index) => ({
                ...identification,
                id: index,
              }))}
              renderItem={(item) => (
                <HighlightListItem
                  actions={
                    cardHolderId !== undefined && cardHolderId !== null ? (
                      [<a key="address-edit"
                        onClick={(e) => {
                          e.preventDefault();
                          editIdentificationExecute(item);
                        }}><EditOutlined /></a>,
                      <ButtonDelete
                        record={item}
                        identifications={identifications}
                        setIdentifications={setIdentifications}
                        cardHolderId={cardHolderId}
                        toast={toast}
                        executePatch={executePatch}
                      />
                      ]
                    ) : (setIdentifications !== undefined) && (
                      [
                        <ButtonDelete
                          record={item}
                          identifications={identifications}
                          setIdentifications={setIdentifications}
                          cardHolderId={null}
                          toast={toast}
                          executePatch={executePatch}
                        />
                      ]
                    )
                  }
                >
                  <List.Item.Meta
                    title={`${item.type}: ${item.number}`}
                    description={`${item.country} - Responsable Inscripto: ${(item.registeredVatPayer) ? 'Si' : 'No'  }`}
                  />
                </HighlightListItem>
              )}
            />
          </>
        ) : (
          <Col span={24} style={{marginTop:'1em'}}>
            <Alert
              message="Debes agregar al menos 1 identificacion"
              type="info"
              showIcon
              style={{ marginBottom: '2rem' }}
            />
          </Col>
        )
      }
      <ModalIdentification
        open={identificationsOpen}
        onCreate={validateAction}
        onCancel={() => setModalIdentificationsOpen(false)}
        identification={editIdentification}
      />
    </>
  );
};

export default Identifications;
